'use client';

import { useTranslations } from 'next-intl';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { getImageUrl } from '@/lib/image';
import { formatLastLogin } from '../mappers/clients.mapper';
import type { Client } from '../types';

interface ClientDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client: Client | null;
}

const STATUS_STYLES: Record<string, string> = {
  active: 'border-sky-500/30 bg-sky-500/10 text-sky-600 dark:text-sky-400',
  inactive: 'border-gray-500/30 bg-gray-500/10 text-gray-600 dark:text-gray-400'
};

export function ClientDetailDialog({
  open,
  onOpenChange,
  client
}: ClientDetailDialogProps) {
  const t = useTranslations('clients');

  if (!client) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[480px]'>
        <DialogHeader>
          <DialogTitle>{t('viewDetails')}</DialogTitle>
        </DialogHeader>

        <div className='space-y-6 py-4'>
          {/* Logo + Name */}
          <div className='flex items-center gap-4'>
            <Avatar className='h-16 w-16 rounded-lg'>
              {client.logo_url && (
                <AvatarImage
                  src={getImageUrl(client.logo_url)}
                  alt={client.name}
                  className='object-cover'
                />
              )}
              <AvatarFallback className='rounded-lg text-xl font-bold'>
                {client.name.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <h3 className='text-lg font-semibold'>{client.name}</h3>
              <p className='text-muted-foreground text-sm'>
                {client.client_id}
              </p>
            </div>
          </div>

          {/* Details */}
          <div className='space-y-3'>
            <DetailRow label={t('adminEmail')}>
              <a
                href={`mailto:${client.admin_email}`}
                className='text-muted-foreground hover:text-foreground hover:underline'
              >
                {client.admin_email}
              </a>
            </DetailRow>

            <DetailRow label={t('status')}>
              <Badge
                variant='outline'
                className={cn(
                  'text-xs uppercase',
                  STATUS_STYLES[client.status]
                )}
              >
                {client.status === 'active'
                  ? t('statusActive')
                  : t('statusInactive')}
              </Badge>
            </DetailRow>

            <DetailRow label={t('lastLogin')}>
              {formatLastLogin(client.last_login_at) ?? t('never')}
            </DetailRow>

            {client.notes && (
              <DetailRow label={t('notes')}>{client.notes}</DetailRow>
            )}

            <DetailRow label={t('createdAt')}>
              {new Date(client.created_at).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
              })}
            </DetailRow>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DetailRow({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className='flex items-start justify-between gap-4'>
      <span className='text-muted-foreground shrink-0 text-sm'>{label}</span>
      <span className='text-end text-sm'>{children}</span>
    </div>
  );
}
